import type {
  EpocusExam,
  ExamAmendment,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamWorksheetData } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";

export type AmendmentKind = ExamAmendment["kind"];

/**
 * The exam with one amendment or confirmation appended. The server accepts a
 * change to a signed report only in this shape: an amendment carries the
 * worksheet it replaces, and a confirmation leaves the worksheet as it is.
 */
export const appendAmendment = ({
  exam,
  kind,
  reason,
  worksheet,
  images = null,
  id,
  now,
  userId,
  reviewId,
}: {
  exam: EpocusExam;
  kind: AmendmentKind;
  reason: string;
  worksheet: ExamWorksheetData;
  // Image edits made while amending, such as a needle-in-target mark.
  images?: EpocusExam["images"] | null;
  id: string;
  now: string;
  userId: string;
  reviewId: string | null;
}): EpocusExam => ({
  ...exam,
  worksheetData: kind === "AMEND" ? worksheet : exam.worksheetData,
  images: kind === "AMEND" && images ? images : exam.images,
  amendments: [
    ...exam.amendments,
    {
      id,
      kind,
      reason: reason.trim(),
      signedAt: now,
      signedByUserId: userId,
      reviewId,
      previousWorksheet: kind === "AMEND" ? exam.worksheetData : null,
    },
  ],
  meta: { ...exam.meta, updatedAt: now, updatedByUserId: userId },
});

export type ReportVersion = {
  key: string;
  label: string;
  worksheet: ExamWorksheetData;
};

/**
 * Every version of the signed report, oldest first, the last being the
 * current one. A confirmation adds no version: it re-signs the one before.
 */
export const reportVersions = (exam: EpocusExam): ReportVersion[] => {
  const amends = exam.amendments.filter(
    (
      amendment,
    ): amendment is ExamAmendment & {
      previousWorksheet: ExamWorksheetData;
    } => amendment.kind === "AMEND" && amendment.previousWorksheet !== null,
  );
  if (amends.length === 0) return [];

  return [
    ...amends.map((amendment, index) => ({
      key: `before-${amendment.id}`,
      label: index === 0 ? "Original" : `Amendment ${index}`,
      worksheet: amendment.previousWorksheet,
    })),
    {
      key: "current",
      label: `Current (amendment ${amends.length})`,
      worksheet: exam.worksheetData,
    },
  ];
};
